// Audio playback of clips: server audio (Range-capable URL) or, while the upload is still queued,
// the blob from the outbox.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { audioUrl, type ViewClip } from '../lib/api';
import { localAudioUrl } from '../lib/outbox';

/** `src` for a clip: server URL, an object URL of the queued upload, or null (no audio). */
export function useClipAudioSrc(clip: Pick<ViewClip, 'id' | 'hasAudio' | 'localPending'> | null): string | null {
  const id = clip?.id ?? null;
  const hasAudio = clip?.hasAudio ?? false;
  const local = clip?.localPending === true;
  const [localSrc, setLocalSrc] = useState<{ id: string; url: string } | null>(null);

  useEffect(() => {
    if (!id || !hasAudio || !local) return;
    let cancelled = false;
    let url: string | null = null;
    localAudioUrl(id).then(
      (u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        url = u;
        if (u) setLocalSrc({ id, url: u });
      },
      (err: unknown) => console.warn('[audio] local audio unavailable', err),
    );
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setLocalSrc((cur) => (cur?.url === url ? null : cur));
    };
  }, [id, hasAudio, local]);

  if (!id || !hasAudio) return null;
  if (!local) return audioUrl(id);
  return localSrc?.id === id ? localSrc.url : null;
}

export interface SeekRequest {
  ms: number;
  /** Changes on every request so seeking to the same position twice works. */
  nonce: number;
}

interface AudioPlayerProps {
  clip: ViewClip | null;
  seek?: SeekRequest | null;
  /** Current playback position (ms), reported a few times per second. */
  onTime?: (ms: number) => void;
  /** Extra controls shown next to the player (e.g. part selector). */
  children?: ReactNode;
  label?: string;
}

/** Sticky bottom player for the meeting transcript. */
export function AudioPlayer({ clip, seek, onTime, children, label }: AudioPlayerProps) {
  const src = useClipAudioSrc(clip);
  const ref = useRef<HTMLAudioElement>(null);
  const pendingSeek = useRef<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  useEffect(() => {
    if (!seek) return;
    const el = ref.current;
    if (!el) {
      // The source is still being resolved (queued upload); seek once it is loaded.
      pendingSeek.current = seek.ms;
      return;
    }
    const apply = (ms: number) => {
      try {
        el.currentTime = ms / 1000;
      } catch {
        // Not seekable yet; the position is applied on loadedmetadata.
        pendingSeek.current = ms;
        return;
      }
      void el.play().catch(() => {
        // Autoplay restrictions: the user can press play in the controls.
      });
    };
    if (el.readyState >= 1) apply(seek.ms);
    else pendingSeek.current = seek.ms;
  }, [seek]);

  if (!clip || !clip.hasAudio) return null;

  return (
    <div className="player" role="region" aria-label="Predvajalnik posnetka">
      <div className="player__inner">
        {(label || children) && (
          <div className="player__head">
            {label && <span className="player__label">{label}</span>}
            {children}
          </div>
        )}
        {src ? (
          <audio
            ref={ref}
            className="player__audio"
            src={src}
            controls
            preload="metadata"
            onLoadedMetadata={(e) => {
              const ms = pendingSeek.current;
              pendingSeek.current = null;
              if (ms === null) return;
              e.currentTarget.currentTime = ms / 1000;
              void e.currentTarget.play().catch(() => {});
            }}
            onTimeUpdate={(e) => onTime?.(e.currentTarget.currentTime * 1000)}
            onSeeked={(e) => onTime?.(e.currentTarget.currentTime * 1000)}
            onError={() => setFailed(true)}
          />
        ) : (
          <p className="player__missing">Posnetek nalagam …</p>
        )}
        {failed && <p className="player__error">Posnetka ni bilo mogoče predvajati.</p>}
      </div>
    </div>
  );
}

/** Inline player (expanded note). */
export function ClipAudio({ clip }: { clip: ViewClip }) {
  const src = useClipAudioSrc(clip);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!clip.hasAudio) return null;
  if (!src) return <p className="muted small">Posnetek nalagam …</p>;
  return (
    <div className="clip-audio">
      <audio src={src} controls preload="metadata" onError={() => setFailed(true)} />
      {failed && <p className="player__error">Posnetka ni bilo mogoče predvajati.</p>}
    </div>
  );
}
